import { useState } from 'react';
import {
	FieldErrors,
	FieldValues,
	UseFormHandleSubmit,
	UseFormRegister,
	UseFormSetError,
	UseFormSetValue,
	UseFormWatch,
} from 'react-hook-form';

import {
	Confirm,
	GridContainer,
	Input,
	Modal,
	SliderCheckbox,
	SpinnerOverlay,
	SubmissionButtons,
	validationErrMessages,
} from '@components/common';

import { IUserFormValues } from '@interfaces/components/form';

import { MODAL_WIDTH_500 } from '@constants/modals';
import { regExpLetters } from '@constants/regExp';
import { EUserMultipleOrganisationsFormFields, userFormAreas, userFormTabNames } from '../constants';

import styles from '../UserMultipleOrganisationsForm.module.scss';

interface UserMultipleOrganisationsBasicInfoProps {
	errors: FieldErrors<IUserFormValues>;
	isLoading: boolean;
	phoneNumber: string;
	isEmailAlreadyExists: boolean;
	isSingleOrganisationMode: boolean;
	isViewMode: boolean;
	isEditMode: boolean;
	isSubmitDisabled: boolean;
	setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	onClose: () => void;
	setCurrentTab: (tab: string) => void;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	setError: UseFormSetError<FieldValues>;
	handleSubmit: UseFormHandleSubmit<FieldValues, undefined>;
	watch: UseFormWatch<FieldValues>;
}

const UserMultipleOrganisationsBasicInfo = ({
	errors,
	phoneNumber,
	isLoading,
	isEmailAlreadyExists,
	isSingleOrganisationMode,
	isSubmitDisabled,
	isViewMode,
	isEditMode,
	setIsEditMode,
	onClose,
	setCurrentTab,
	register,
	setValue,
	setError,
	handleSubmit,
	watch,
}: UserMultipleOrganisationsBasicInfoProps) => {
	const isFieldsDisabled = isViewMode && !isEditMode;
	// temporary until the 2FA functionality is implemented
	const isDisabled2FA = !watch(EUserMultipleOrganisationsFormFields.is2FA);

	const onChangePhoneNumber = (phone: string) => {
		const formattedPhone = phone.slice(1);

		setValue(EUserMultipleOrganisationsFormFields.phoneNumber, formattedPhone);
	};

	const confirmHandler = () => {
		setCurrentTab(isSingleOrganisationMode
			? userFormTabNames.assignForm
			: userFormTabNames.organisationAssignment);
	};

	const confirmUpdateUserHandler = () => {
		if (isSingleOrganisationMode) {
			setIsEditMode(true);
		}

		setCurrentTab(isSingleOrganisationMode
			? userFormTabNames.assignForm
			: userFormTabNames.organisationAssignment);
	};

	const negativeHandler = () => {
		if (isFieldsDisabled) {
			setIsEditMode(true);

			return;
		}

		onClose();
	};

	const [isConfirm, setIsConfirm] = useState(false);

	const positiveHandler = () => {
		if (isFieldsDisabled) {
			onClose();

			return;
		}

		if (!isEmailAlreadyExists) {
			confirmHandler();
		} else {
			setIsConfirm(true);
		}
	};

	const closeConfirm = () => {
		setIsConfirm(false);

		setError(EUserMultipleOrganisationsFormFields.email, { type: 'manual', message: validationErrMessages.emailUnique });
	};

	return (
			<GridContainer
				columns={2}
				customClassName={styles.userFormContainer}
				gap='15px 20px'
				gridTemplateAreas={userFormAreas}
			>
				{isLoading && <SpinnerOverlay/>}
				<Modal
					isOpen={isConfirm}
					onClose={closeConfirm}
					width={MODAL_WIDTH_500}
				>
					<Confirm
						negativeBtnText='Create new user'
						onClose={closeConfirm}
						onSubmit={confirmUpdateUserHandler}
						positiveBtnText='Add user to organisation'
					>
						The user with the provided email already exists on the platform.
						<br/>
						Please choose how you would like to proceed.
					</Confirm>
				</Modal>
				<div className={styles.firstName}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.firstName?.message}
						label='First Name'
						name={EUserMultipleOrganisationsFormFields.firstName}
						placeholder='Enter first name'
						regexValidation={regExpLetters}
						register={register}
						required
						setValue={setValue}
						withCustomValidation
					/>
				</div>
				<div className={styles.lastName}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.lastName?.message}
						label='Last Name'
						name={EUserMultipleOrganisationsFormFields.lastName}
						placeholder='Enter last name'
						regexValidation={regExpLetters}
						register={register}
						required
						setValue={setValue}
						withCustomValidation
					/>
				</div>
				<div className={styles.email}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.email?.message}
						label='Email Address'
						name={EUserMultipleOrganisationsFormFields.email}
						placeholder='Enter email'
						register={register}
						required
					/>
				</div>
				<div className={styles.contactNumber}>
					<Input
						disabled={isFieldsDisabled}
						errorMessage={errors?.phoneNumber?.message}
						isPhoneNumber
						label='Contact Number'
						name={EUserMultipleOrganisationsFormFields.phoneNumber}
						onChange={onChangePhoneNumber}
						placeholder='Enter contact number'
						value={phoneNumber}
					/>
				</div>
				<div className={styles.fa}>
					<SliderCheckbox
						disabled={isFieldsDisabled || isDisabled2FA}
						id='is2FA'
						isLabelAfter
						label='Enable Two-Factor Authentication (2FA)'
						name={EUserMultipleOrganisationsFormFields.is2FA}
						register={register}
					/>
				</div>
				<SubmissionButtons
					className={styles.submitButtons}
					disabledPositive={isSubmitDisabled}
					negativeHandler={negativeHandler}
					negativeLabel={isFieldsDisabled ? 'Edit user' :'Cancel'}
					positiveHandler={handleSubmit(positiveHandler)}
					positiveLabel={isFieldsDisabled ? 'Close' : 'Next'}
				/>
			</GridContainer>
	);
};

export default UserMultipleOrganisationsBasicInfo;
